import { formatDate } from '@/lib/dates'
import { ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useSetTrackerStatus, useUpdateTracker, useAdminPatch, extractErrorMessage } from '@/queries/ticketMutations'
import { useTrackerCategories } from '@/queries/tracker'
import { isAdminViewer } from '@/lib/scope'
import {
  trackerStatusLabels,
  trackerStatusVariants,
  trackerStatusOrder,
  devStageLabels,
  devStageVariants,
  priorityLabels,
  priorityVariants,
} from '@/lib/status'
import type { TrackerTicket, TrackerStatus } from '@/types/tracker'
import type { TicketPriority, ViewerRelation } from '@/types/ticket'

// Read-only pills in the band sit level with the adjacent 30px selects
// (design doc "Tracked issue detail"). Badge's own defaults (11px micro
// text, ~20px tall) read as a small table pill next to a select at this
// height, so every band pill forces the control-height-sm ramp — Badge,
// SelectTrigger and Input each bake in their own conflicting height/padding
// utility that a plain className can't out-specificity, hence the `!`.
const bandPillClass = 'h-[var(--etn-control-height-sm)]! items-center rounded-etn-pill px-3.5! py-0! text-etn-control! font-semibold'
const bandSelectClass =
  'h-[var(--etn-control-height-sm)] rounded-etn-pill border border-etn-border-input bg-etn-surface px-3.5 text-etn-control font-semibold text-etn-text-cell outline-none focus-visible:border-etn-accent'

function Prop({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="mb-[5px] text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">{label}</dt>
      <dd className="font-medium text-etn-text-cell">{children}</dd>
    </div>
  )
}

// Item 2 of the issue-detail rebuild: TrackerPanel's old vertical sidebar
// Row list, relocated into the mockup's horizontal property band directly
// under the header. Every gating rule and mutation is unchanged from that
// panel — only the layout moved (see TrackerPropertyBand.test.tsx /
// .dates.test.tsx / .errors.test.tsx, migrated 1:1 from TrackerPanel's own
// suite). Impact moved out to its own TrackerImpactSection — the band mirrors
// the mockup's five propband columns (Status/Priority/Progress/Category/
// Expected) plus the Linear link and Promoted date, which the mockup omits
// but existing tests still pin.
export function TrackerPropertyBand({
  ticket,
  viewerRelation,
}: {
  ticket: TrackerTicket
  viewerRelation: ViewerRelation
}) {
  const isStaff = isAdminViewer(viewerRelation)
  const setStatus = useSetTrackerStatus(ticket._id)
  const updateTracker = useUpdateTracker(ticket._id)
  const patchTicket = useAdminPatch(ticket._id)
  // Disabled for a client via the undefined companyId arg — nobody but staff
  // edits category, so nobody but staff needs the list fetched.
  const categoriesQuery = useTrackerCategories(isStaff ? ticket.companyId : undefined)
  const categories = categoriesQuery.data ?? []
  const { tracker } = ticket

  const changeStatus = async (status: TrackerStatus) => {
    try {
      await setStatus.mutateAsync({ status })
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not update status'))
    }
  }

  // Whitelist-shaped like the server's PATCH /:id/tracker — one field per
  // call, so devStage/status (their own dedicated paths) can never be smuggled
  // in alongside a category/expected edit.
  const patchTracker = async (input: Parameters<typeof updateTracker.mutateAsync>[0]) => {
    try {
      await updateTracker.mutateAsync(input)
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not update tracker'))
    }
  }

  const changePriority = async (priority: TicketPriority) => {
    try {
      await patchTicket.mutateAsync({ priority })
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not update priority'))
    }
  }

  // Only staff can pick freely; a client's sole write path is the
  // proposed_closed confirm/reject pair rendered below (design doc "Access
  // and scoping") — every other status is display-only for them.
  const clientCanAct = !isStaff && tracker.status === 'proposed_closed'

  return (
    <div className="my-4 space-y-2">
      <dl className="flex flex-wrap items-start gap-x-[26px] gap-y-3 rounded-lg bg-muted px-[18px] py-[14px]">
        <Prop label="Status">
          {isStaff ? (
            <select
              aria-label="Tracker status"
              value={tracker.status}
              disabled={setStatus.isPending}
              onChange={e => changeStatus(e.target.value as TrackerStatus)}
              className={bandSelectClass}
            >
              {trackerStatusOrder.map(s => (
                <option key={s} value={s}>
                  {trackerStatusLabels[s]}
                </option>
              ))}
            </select>
          ) : (
            <Badge variant={trackerStatusVariants[tracker.status]} className={bandPillClass}>
              {trackerStatusLabels[tracker.status]}
            </Badge>
          )}

          {clientCanAct && (
            <div className="mt-2 flex gap-2">
              <Button size="xs" disabled={setStatus.isPending} onClick={() => changeStatus('closed')}>
                Confirm closed
              </Button>
              <Button size="xs" variant="outline" disabled={setStatus.isPending} onClick={() => changeStatus('accepted')}>
                Reopen
              </Button>
            </div>
          )}
        </Prop>

        <Prop label="Priority">
          {isStaff ? (
            <select
              aria-label="Priority"
              value={ticket.priority}
              disabled={patchTicket.isPending}
              onChange={e => changePriority(e.target.value as TicketPriority)}
              className={bandSelectClass}
            >
              {(['low', 'normal', 'high'] as TicketPriority[]).map(p => (
                <option key={p} value={p}>
                  {priorityLabels[p]}
                </option>
              ))}
            </select>
          ) : (
            <Badge variant={priorityVariants[ticket.priority]} className={bandPillClass}>
              {priorityLabels[ticket.priority]}
            </Badge>
          )}
        </Prop>

        <Prop label="Progress">
          <Badge variant={devStageVariants[tracker.devStage]} className={bandPillClass}>
            {devStageLabels[tracker.devStage]}
          </Badge>
        </Prop>

        {(isStaff || tracker.category) && (
          <Prop label="Category">
            {isStaff ? (
              <Select
                value={tracker.category ?? ''}
                onValueChange={value => patchTracker({ category: value })}
                disabled={updateTracker.isPending || categoriesQuery.isLoading}
              >
                <SelectTrigger aria-label="Tracker category" className="h-[var(--etn-control-height-sm)]! w-[180px] text-etn-control">
                  <SelectValue placeholder={categoriesQuery.isLoading ? 'Loading categories…' : 'Choose a category'} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="inline-flex h-[var(--etn-control-height-sm)] items-center">{tracker.category}</span>
            )}
          </Prop>
        )}

        {(isStaff || tracker.expectedDate) && (
          <Prop label="Expected">
            {isStaff ? (
              <Input
                type="date"
                aria-label="Tracker expected date"
                className="h-[var(--etn-control-height-sm)]! w-auto text-etn-control"
                // Plain slicing, not dayjs formatting/parsing, on both sides
                // of this round-trip: dayjs parses a bare 'YYYY-MM-DD' as
                // LOCAL midnight, so in any positive-UTC-offset timezone
                // (all of Europe in summer, for one) dayjs(value).toISOString()
                // rolls the date back a day. The stored value is already an
                // ISO string, so its first 10 characters ARE the calendar
                // date — no timezone math needed to read it back into the
                // input, and `new Date(value)` (a bare date, no time) is UTC
                // midnight per spec, unlike dayjs's parse of the same string.
                value={tracker.expectedDate ? tracker.expectedDate.slice(0, 10) : ''}
                disabled={updateTracker.isPending}
                onChange={e => {
                  const value = e.target.value
                  patchTracker({ expectedDate: value ? new Date(value).toISOString() : null })
                }}
              />
            ) : (
              // Calendar-date semantics: format the UTC date string directly. dayjs()
              // converts to local time first, which shows the previous day anywhere
              // west of UTC.
              <span className="inline-flex h-[var(--etn-control-height-sm)] items-center">
                {formatDate(tracker.expectedDate!.slice(0, 10))}
              </span>
            )}
          </Prop>
        )}
      </dl>

      {/* Not in the mockup's propband, but Linear/Promoted are existing,
          test-pinned functionality — kept as a quiet meta line under the
          band rather than dropped. */}
      <div className="flex items-center gap-4 px-1 text-etn-micro text-etn-text-soft">
        {ticket.linearIssueUrl && (
          <a
            href={ticket.linearIssueUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-etn-text-cell hover:underline"
          >
            View issue <ExternalLink className="h-3 w-3" />
          </a>
        )}
        <span>
          Promoted <span className="text-etn-text-cell">{formatDate(tracker.promotedAt)}</span>
        </span>
      </div>
    </div>
  )
}
